import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { canAny, isSuperadmin } from '../api/client';
import { useMe } from '../features/auth/session';
import { AlarmsPage } from '../features/alarms/AlarmsPage';
import { AssetsPage } from '../features/assets/AssetsPage';
import { ConfigurationPage } from '../features/configuration/ConfigurationPage';
import { GroupsPage } from '../features/groups/GroupsPage';
import { InventoryPage } from '../features/inventory/InventoryPage';
import { ManualsPage } from '../features/manuals/ManualsPage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { ReportViewPage } from '../features/reports/ReportViewer';
import { Home } from './Home';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { DeviceDetailPage } from '../features/devices/DeviceDetailPage';
import { DevicesPage } from '../features/devices/DevicesPage';
import { DiscoveredPage } from '../features/discovery/DiscoveredPage';
import { GatewaysPage } from '../features/gateways/GatewaysPage';
import { ConcentratorsPage } from '../features/infrastructure/ConcentratorsPage';
import { MapPage } from '../features/map/MapPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { AreaEditor } from '../features/sites/AreaEditor';
import { SitesPage } from '../features/sites/SitesPage';
import { UsersPage } from '../features/users/UsersPage';
import { AuthGate } from '../features/auth/AuthGate';
import { RealtimeProvider } from '../realtime/RealtimeProvider';
import { AppShell } from './AppShell';

// Server state: no background refetch loops; data changes arrive as realtime deltas instead (Rule 12).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, refetchInterval: false, staleTime: 30_000, retry: 1 },
  },
});

/**
 * A page reached by URL without the permission its rail entry needs goes back to the start page (the server refuses
 * its data anyway): e.g. the authority observer has no locations, concentrators, alarms or groups pages.
 */
function Requires({ perm, children }: { perm: string[]; children: ReactNode }) {
  const me = useMe().data;
  if (me && !perm.every((p) => canAny(me, p))) return <Navigate to="/" replace />;
  return <>{children}</>;
}
/** Superadmin-only pages (the server refuses their data to anyone else). */
function SuperadminOnly({ children }: { children: ReactNode }) {
  const me = useMe().data;
  if (me && !isSuperadmin(me)) return <Navigate to="/" replace />;
  return <>{children}</>;
}
const ops = (el: ReactNode, ...perm: string[]) => (
  <Requires perm={['operations.view', ...perm]}>{el}</Requires>
);
const need = (el: ReactNode, ...perm: string[]) => <Requires perm={perm}>{el}</Requires>;

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <Home dashboard={<DashboardPage />} /> },
      { path: 'map', element: <MapPage /> },
      { path: 'devices', element: <DevicesPage /> },
      { path: 'devices/:id', element: <DeviceDetailPage /> },
      { path: 'discovered', element: <DiscoveredPage /> },
      { path: 'sites', element: ops(<SitesPage />, 'site.view') },
      { path: 'sites/:id/area', element: ops(<AreaEditor />, 'site.view') },
      { path: 'gateways', element: <GatewaysPage /> },
      { path: 'infrastructure', element: ops(<ConcentratorsPage />) },
      { path: 'alarms', element: need(<AlarmsPage />, 'alarm.view') },
      { path: 'admin/users', element: <UsersPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'settings/:tab', element: <SettingsPage /> },
      { path: 'reports', element: <ReportsPage /> },
      { path: 'reports/:tab', element: <ReportsPage /> },
      { path: 'reports/view/:id', element: <ReportViewPage /> },
      { path: 'groups', element: need(<GroupsPage />, 'group.view') },
      { path: 'groups/:tab', element: need(<GroupsPage />, 'group.view') },
      { path: 'configuration', element: <ConfigurationPage /> },
      { path: 'configuration/:tab', element: <ConfigurationPage /> },
      { path: 'assets', element: <AssetsPage /> },
      { path: 'inventory', element: <InventoryPage /> },
      {
        path: 'manuals',
        element: (
          <SuperadminOnly>
            <ManualsPage />
          </SuperadminOnly>
        ),
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthGate>
        <RealtimeProvider>
          <RouterProvider router={router} />
        </RealtimeProvider>
      </AuthGate>
    </QueryClientProvider>
  );
}
